/**
 * 优惠券抢购成功弹窗组件
 * 用于显示抢购成功后的优惠券信息
 */
if (!customElements.get('marketing-coupon-snap-up-coupon')) {
  customElements.define(
    'marketing-coupon-snap-up-coupon',
    class MarketingCouponSnapUpCoupon extends HTMLElement {
      constructor() {
        super();
        this.discountCode = null;
        this.expiredAt = null;
        this.countdownInterval = null;
      }

      connectedCallback() {
        this.init();
      }

      /**
       * 初始化组件
       */
      init() {
        // 关闭按钮事件（关闭图标）
        const closeBtn = this.querySelector('.marketing-coupon-snap-up-coupon-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            this.closeModal();
          });
        }

        // 取消按钮事件
        const cancelBtn = this.querySelector('.marketing-coupon-snap-up-coupon-btn-cancel');
        if (cancelBtn) {
          cancelBtn.addEventListener('click', () => {
            this.closeModal();
          });
        }

        // 优惠码复制按钮事件
        const codeCopyBtn = this.querySelector('.marketing-coupon-snap-up-coupon-code-copy-btn');
        if (codeCopyBtn) {
          codeCopyBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.copyCodeOnly();
          });
        }

        // 复制并去购物按钮事件
        const copyBtn = this.querySelector('.marketing-coupon-snap-up-coupon-btn-copy button');
        if (copyBtn) {
          copyBtn.addEventListener('click', () => {
            const url = copyBtn.getAttribute('data-url');
            this.copyCode(url);
          });
        }

        // 监听滚动，处理底部按钮显示
        const scrollContainer = this.querySelector('.member-redeem-scroll-container');
        if (scrollContainer) {
          scrollContainer.addEventListener('scroll', () => {
            this.handleScroll();
          });
        }
      }

      /**
       * 显示弹窗
       * @param {Object} data - API 返回的数据
       */
      showModal(data) {
        if (!data) return;

        // 显示弹窗
        this.classList.remove('hidden');
        this.classList.add('flex');
        window.litime?.disableScroll();

        // 设置图片
        const imgEl = this.querySelector('.marketing-coupon-snap-up-coupon-img');
        if (imgEl && data.icon) {
          imgEl.src = data.icon;
          imgEl.alt = data.rewardName || 'Coupon';
        }

        // 设置标题
        const titleEl = this.querySelector('.marketing-coupon-snap-up-coupon-title');
        if (titleEl) {
          titleEl.textContent = data.rewardName || '';
        }

        // 隐藏积分显示（抢购场景不需要显示积分）
        const pointsEl = this.querySelector('.marketing-coupon-snap-up-coupon-points');
        if (pointsEl) {
          pointsEl.classList.add('hidden');
        }

        // 显示优惠码
        this.discountCode = data.discountCode || null;
        const codeEl = this.querySelector('.marketing-coupon-snap-up-coupon-code');
        if (codeEl && this.discountCode) {
          codeEl.classList.remove('hidden');
          codeEl.classList.add('flex');
          const codeValueSpan = codeEl.querySelector('.marketing-coupon-snap-up-coupon-code-value');
          if (codeValueSpan) {
            codeValueSpan.textContent = this.discountCode;
          }
        } else if (codeEl) {
          // 如果没有优惠码，隐藏优惠码区域
          codeEl.classList.add('hidden');
          codeEl.classList.remove('flex');
        }

        // 设置过期时间并启动倒计时
        if (data.expiredAt) {
          this.expiredAt = data.expiredAt;
          this.initCountdown();
        } else {
          // 如果没有过期时间，隐藏倒计时
          const countdownEl = this.querySelector('.marketing-coupon-snap-up-coupon-countdown');
          if (countdownEl) {
            countdownEl.classList.add('hidden');
          }
          this.clearCountdown();
        }

        // 显示底部按钮区域
        const btnContainer = this.querySelector('.marketing-coupon-snap-up-coupon-btn');
        if (btnContainer) {
          btnContainer.classList.remove('hidden');
          btnContainer.classList.add('flex');
        }

        // 隐藏兑换按钮区域，显示复制按钮
        const redeemWrapper = this.querySelector('.marketing-coupon-snap-up-coupon-btn-redeem_wrapper');
        const copyBtnContainer = this.querySelector('.marketing-coupon-snap-up-coupon-btn-copy');
        if (redeemWrapper) {
          redeemWrapper.classList.add('hidden');
        }
        if (copyBtnContainer) {
          copyBtnContainer.classList.remove('hidden');
          copyBtnContainer.classList.add('flex');
        }

        // 使用 requestAnimationFrame 确保 DOM 更新后再处理
        requestAnimationFrame(() => {
          // 处理滚动
          this.handleScroll();
        });
      }

      /**
       * 关闭弹窗
       */
      closeModal() {
        this.classList.add('hidden');
        this.classList.remove('flex');
        window.litime?.enableScroll();
        // 清理倒计时
        this.clearCountdown();
      }

      /**
       * 仅复制优惠码（不跳转）
       */
      copyCodeOnly() {
        if (!this.discountCode) return;

        // 优先使用 window.litime.copyToClipboard
        if (window.litime?.copyToClipboard) {
          window.litime.copyToClipboard(this.discountCode);

          return;
        }
      }

      /**
       * 降级复制方法（兼容旧浏览器）
       */
      fallbackCopyCode() {
        const textArea = document.createElement('textarea');
        textArea.value = this.discountCode;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand('copy');
          if (successful && window.litime?.showToast) {
            window.litime.showToast('success', 'Code copied!');
          } else if (window.litime?.showToast) {
            window.litime.showToast('error', 'Failed to copy code');
          }
        } catch (err) {
          console.error('Fallback copy failed:', err);
          if (window.litime?.showToast) {
            window.litime.showToast('error', 'Failed to copy code');
          }
        } finally {
          document.body.removeChild(textArea);
        }
      }

      /**
       * 复制优惠码并跳转
       * @param {string} url - 跳转的 URL（可选）
       */
      async copyCode(url) {
        if (!this.discountCode) return;
        console.log(this.discountCode);

        // 如果有 URL，直接跳转到 discount URL（GET 请求）
        if (url && url.trim() !== '') {
          // 复制优惠码到剪贴板
          window.litime.copyToClipboard(this.discountCode);
          // 直接跳转到 discount URL（GET 请求，无参数）
          const redirecturl = `/discount/${this.discountCode}?redirect=${url}`;
          window.location.href = redirecturl;
        } else {
          // 如果没有 URL，复制成功后关闭弹窗
          window.litime.copyToClipboard(this.discountCode);
          this.closeModal();
        }
      }

      /**
       * 初始化倒计时
       */
      initCountdown() {
        // 清理之前的倒计时
        this.clearCountdown();

        if (!this.expiredAt) return;

        // 显示倒计时区域
        const countdownEl = this.querySelector('.marketing-coupon-snap-up-coupon-countdown');
        if (countdownEl) {
          countdownEl.classList.remove('hidden');
        }

        // 更新倒计时
        this.updateCountdown();

        // 每秒更新一次
        this.countdownInterval = setInterval(() => {
          this.updateCountdown();
        }, 1000);
      }

      /**
       * 更新倒计时显示
       */
      updateCountdown() {
        if (!this.expiredAt) return;
        const countdownValueEl = this.querySelector('.marketing-coupon-snap-up-coupon-countdown-value');
        if (!countdownValueEl) return;

        // 解析过期时间（格式：2025-11-14 00:00:00 或 2025-11-14T00:00:00）
        let expiredDate;
        try {
          // 尝试直接解析
          expiredDate = new Date(this.expiredAt);

          // 如果解析失败，尝试替换格式
          if (isNaN(expiredDate.getTime())) {
            // 替换空格为 T，添加时区信息（如果需要）
            const dateStr = this.expiredAt.replace(/\s+/, 'T');
            expiredDate = new Date(dateStr);
          }

          // 如果还是失败，尝试替换 - 为 /
          if (isNaN(expiredDate.getTime())) {
            expiredDate = new Date(this.expiredAt.replace(/-/g, '/'));
          }
        } catch (error) {
          console.error('Failed to parse expiredAt:', this.expiredAt, error);
          countdownValueEl.textContent = '00:00:00';
          this.clearCountdown();
          return;
        }

        const now = new Date();
        const timeDifference = expiredDate.getTime() - now.getTime();

        // 找到倒计时区域的父元素
        const countdownContainer = countdownValueEl.closest('.marketing-coupon-snap-up-coupon-countdown');

        if (isNaN(timeDifference) || timeDifference <= 0) {
          // 已过期或解析失败（倒计时为0），隐藏倒计时区域
          if (countdownContainer) {
            countdownContainer.style.display = 'none';
          }
          countdownValueEl.textContent = '00:00:00';
          this.clearCountdown();
          return;
        }

        // 显示倒计时区域
        if (countdownContainer) {
          countdownContainer.style.display = '';
        }

        // 计算时分秒
        const hours = Math.floor(timeDifference / (1000 * 60 * 60));
        const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

        // 格式化为 HH:MM:SS
        const formattedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
          seconds
        ).padStart(2, '0')}`;
        countdownValueEl.textContent = formattedTime;
      }

      /**
       * 清理倒计时
       */
      clearCountdown() {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
      }

      /**
       * 组件卸载时清理
       */
      disconnectedCallback() {
        this.clearCountdown();
      }

      /**
       * 处理滚动，调整底部按钮样式
       */
      handleScroll() {
        const scrollContainer = this.querySelector('.member-redeem-scroll-container');
        const copyBtnContainer = this.querySelector('.marketing-coupon-snap-up-coupon-btn-copy');
        const btnContainer = this.querySelector('.marketing-coupon-snap-up-coupon-btn');

        if (!scrollContainer || !copyBtnContainer || !btnContainer) return;

        const isScrolledToBottom =
          scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 5;

        if (isScrolledToBottom) {
          // 滚动到底部，显示边框，移除阴影
          copyBtnContainer.classList.add('border-t');
          btnContainer.style.boxShadow = 'none';
        } else {
          // 未滚动到底部，隐藏边框，显示阴影
          copyBtnContainer.classList.remove('border-t');
          btnContainer.style.boxShadow = '0 0 4px 0px #0000001a';
        }
      }
    }
  );
}
